import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Stack,
    CircularProgress,
    Button,
    Grid,
    TextField,
    MenuItem,
    Alert,
    IconButton,
    Switch,
    FormControlLabel
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreatePromotion, useUpdatePromotion, usePromotionDetail } from 'hooks/usePromotions';

const PromotionForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = id && id !== 'create';

    const { data: promotionData, isLoading: isLoadingPromo } = usePromotionDetail(isEditMode ? id : undefined);
    const createMutation = useCreatePromotion();
    const updateMutation = useUpdatePromotion();

    const [formData, setFormData] = useState({
        title: '',
        code: '',
        description: '',
        discountType: 'percentage', // percentage | amount
        discountValue: '',
        eligibleAmount: 0,
        slots: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isActive: true
    });
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (isEditMode && promotionData) {
            // promotionData might be wrapped in `{ data: {...} }` depending on backend
            // Our hook handles `.then(res => res.data)` so it might be the raw object or `{ data: {} }` wrapper.
            const promo = promotionData.data || promotionData;

            // Format expiry date for date picker YYYY-MM-DD
            const formattedDate = promo.endDate ? new Date(promo.endDate).toISOString().split('T')[0] : '';

            setFormData({
                title: promo.title || '',
                code: promo.code || '',
                description: promo.description || '',
                discountType: promo.discountType || 'percentage',
                discountValue: promo.discountType === 'percentage' ? promo.discountPercentage : promo.discountAmount,
                eligibleAmount: promo.eligibleAmount || 0,
                slots: promo.slots || '',
                startDate: promo.startDate ? new Date(promo.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                endDate: formattedDate,
                isActive: promo.isActive !== false
            });
        }
    }, [isEditMode, promotionData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (!formData.title || !formData.code || !formData.discountValue || !formData.endDate || !formData.slots) {
            setErrorMsg('Please fill in all required fields (Title, Code, Discount Value, End Date, Slots).');
            return;
        }

        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const daysDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));

        const payload = {
            title: formData.title,
            description: formData.description,
            code: formData.code.toUpperCase(),
            discountType: formData.discountType === 'fixed' ? 'amount' : formData.discountType,
            discountPercentage: formData.discountType === 'percentage' ? Number(formData.discountValue) : undefined,
            discountAmount: formData.discountType === 'fixed' ? Number(formData.discountValue) : undefined,
            slots: Number(formData.slots),
            eligibleAmount: Number(formData.eligibleAmount || 0),
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            daysToExpire: daysDifference > 0 ? daysDifference : 1,
            isActive: formData.isActive
        };

        try {
            if (isEditMode) {
                await updateMutation.mutateAsync({ id, payload });
            } else {
                await createMutation.mutateAsync(payload);
            }
            navigate('/admin/promotions');
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || err.message || 'Failed to save promotion');
        }
    };

    if (isEditMode && isLoadingPromo) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                <CircularProgress />
            </Box>
        );
    }

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                <IconButton onClick={() => navigate('/admin/promotions')} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" fontWeight={800} color="primary.main">
                    {isEditMode ? 'Edit Promotion' : 'Create New Promotion'}
                </Typography>
            </Stack>

            <Card sx={{ p: 4, borderRadius: 3, maxWidth: 800 }}>
                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {errorMsg}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Promotion Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="e.g. Summer Delivery Discount"
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Promo Code"
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                placeholder="e.g. SUMMER50"
                                InputProps={{ style: { textTransform: 'uppercase' } }}
                                required
                                disabled={isEditMode}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={<Switch color="primary" name="isActive" checked={formData.isActive} onChange={handleChange} />}
                                label="Active Status"
                                sx={{ mt: 1, ml: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description / Marketing Text"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                placeholder="Get 50% off all deliveries this summer!"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Discount Type"
                                name="discountType"
                                value={formData.discountType}
                                onChange={handleChange}
                            >
                                <MenuItem value="percentage">Percentage (%)</MenuItem>
                                <MenuItem value="fixed">Fixed Amount (₦)</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Discount Value"
                                name="discountValue"
                                value={formData.discountValue}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="End Date"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleChange}
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Total Slots (Usage Limit)"
                                name="slots"
                                value={formData.slots}
                                onChange={handleChange}
                                required
                                placeholder="e.g. 100"
                                helperText="Total number of times this code can be used."
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Minimum Order Value (₦)"
                                name="eligibleAmount"
                                value={formData.eligibleAmount}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} mt={2}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button variant="outlined" onClick={() => navigate('/admin/promotions')} disabled={isPending}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                    disabled={isPending}
                                >
                                    {isPending ? 'Saving...' : 'Save Promotion'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </form>
            </Card>
        </Box>
    );
};

export default PromotionForm;
